import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
    DatabaseFill,
    Upload,
    CheckCircle,
    GearFill,
} from 'react-bootstrap-icons';

import { useProjects } from '../../contexts/ProjectContext';
import { fetchRequirementTestDetail, fetchRequirementTestWorkspace } from '../../api/requirements';
import { RequirementGenerationJob, RequirementArtifact, RequirementTestFolderNode, RequirementWorkspaceTestCase, RequirementTestDetailResponse } from '../../types/requirements';
import apiClient, { API_ENDPOINTS } from '../../api/config';
import TestDirectoryTree from '../requirements/TestDirectoryTree';
import TestResultsList from '../requirements/TestResultsList';
import TestCasePopup from '../requirements/TestCasePopup';
import AddTestCaseModal from '../requirements/AddTestCaseModal';
import ProjectChatPanel from '../chat/ProjectChatPanel';
import { formatFolderPathForDisplay, normalizeFolderPathInput } from '../requirements/FolderPathSelector';
import Modal from 'react-bootstrap/Modal';
import {
    deleteRequirementTestCase,
    deleteRequirementTestFolder,
    renameRequirementTestFolder,
    updateRequirementTestFolder,
} from '../../api/requirements';
import '../requirements/RequirementTestWorkspace.css';

interface SyntheticDataTabProps {
    projectId: number;
}

export function getTestArtifact(job: RequirementGenerationJob | null): RequirementArtifact | null {
    if (!job?.artifacts?.length) return null;
    return job.artifacts.find((artifact) => (artifact.artifact_type || '').toLowerCase() === 'test_cases') || null;
}

const ACTIVE_SYNTHETIC_JOB_STATUSES = new Set(['queued', 'pending', 'running', 'retrying']);
const SAMPLE_FILE_MAX_BYTES = 10 * 1024 * 1024;

function isActiveSyntheticJobStatus(status?: string | null): boolean {
    return ACTIVE_SYNTHETIC_JOB_STATUSES.has((status || '').toLowerCase());
}

const SyntheticDataTab: React.FC<SyntheticDataTabProps> = ({ projectId }) => {
    const navigate = useNavigate();
    const { requirementJobs, fetchRequirementJobs } = useProjects();
    const [loading, setLoading] = useState(false);
    const [testCases, setTestCases] = useState<RequirementWorkspaceTestCase[]>([]);
    const [directory, setDirectory] = useState<RequirementTestFolderNode | null>(null);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [selectedTestKeys, setSelectedTestKeys] = useState<Set<string>>(new Set());
    const [generating, setGenerating] = useState(false);
    const [showCustomSettings, setShowCustomSettings] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);


    // Search and Filters
    const [search, setSearch] = useState('');
    const [numVariants, setNumVariants] = useState<number>(3);
    const [supportingContext, setSupportingContext] = useState<string>('');
    const [sampleFile, setSampleFile] = useState<File | null>(null);
    const [sampleFileContent, setSampleFileContent] = useState<string>('');
    const [maxVariants, setMaxVariants] = useState<number>(50);

    const fetchPublicConfig = useCallback(async () => {
        try {
            const res = await apiClient.get(API_ENDPOINTS.PUBLIC_CONFIG);
            if (res.data?.synthetic_data_max_variants_per_test) {
                setMaxVariants(res.data.synthetic_data_max_variants_per_test);
            }
        } catch (err) {
            console.error('Failed to fetch public config', err);
        }
    }, []);

    useEffect(() => {
        fetchPublicConfig();
    }, [fetchPublicConfig]);

    const totalRows = useMemo(() => selectedTestKeys.size * numVariants, [selectedTestKeys.size, numVariants]);
    const isOverLimit = numVariants > maxVariants;
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('priority');

    // Feature 7: popup preview state
    const [previewTestKey, setPreviewTestKey] = useState<string | null>(null);
    const [previewDetail, setPreviewDetail] = useState<RequirementTestDetailResponse | null>(null);
    const [previewDetailLoading, setPreviewDetailLoading] = useState<boolean>(false);
    const [previewDetailError, setPreviewDetailError] = useState<string | null>(null);
    const [syntheticQueueLock, setSyntheticQueueLock] = useState(false);

    // Feature 7: manual add modal
    const [showAddTestCaseModal, setShowAddTestCaseModal] = useState<boolean>(false);
    const [moveDialog, setMoveDialog] = useState<{
        open: boolean;
        test: RequirementWorkspaceTestCase | null;
        value: string;
        saving: boolean;
        error?: string | null;
    }>({
        open: false,
        test: null,
        value: '',
        saving: false,
        error: null,
    });
    const [renameDialog, setRenameDialog] = useState<{
        open: boolean;
        folder: RequirementTestFolderNode | null;
        value: string;
        saving: boolean;
        error?: string | null;
    }>({
        open: false,
        folder: null,
        value: '',
        saving: false,
        error: null,
    });
    const [deleteTestDialog, setDeleteTestDialog] = useState<{
        open: boolean;
        test: RequirementWorkspaceTestCase | null;
        saving: boolean;
        error?: string | null;
    }>({
        open: false,
        test: null,
        saving: false,
        error: null,
    });
    const [deleteFolderDialog, setDeleteFolderDialog] = useState<{
        open: boolean;
        folder: RequirementTestFolderNode | null;
        saving: boolean;
        error?: string | null;
    }>({
        open: false,
        folder: null,
        saving: false,
        error: null,
    });

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.size === 0) {
            setMessage({ type: 'danger', text: `"${file.name}" is empty and cannot be uploaded.` });
            event.target.value = '';
            return;
        }

        if (file.size > SAMPLE_FILE_MAX_BYTES) {
            setMessage({ type: 'danger', text: `"${file.name}" is too large. Reference files must be 10 MB or smaller.` });
            event.target.value = '';
            return;
        }

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (extension !== 'csv' && extension !== 'tsv') {
            setMessage({ type: 'danger', text: 'Please upload a CSV or TSV file only.' });
            event.target.value = '';
            return;
        }

        setMessage(null);
        setSampleFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            setSampleFileContent(e.target?.result as string);
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    const jobs = useMemo(() =>
        (requirementJobs[projectId] || [])
            .slice()
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
        [projectId, requirementJobs]);

    const syntheticJobs = useMemo(() =>
        jobs.filter(j => (j.generation_kind || '').toLowerCase() === 'synthetic_data'),
        [jobs]);

    const hasActiveSyntheticJob = useMemo(() =>
        syntheticJobs.some(j => isActiveSyntheticJobStatus(j.status)),
        [syntheticJobs]);

    useEffect(() => {
        if (hasActiveSyntheticJob) {
            setSyntheticQueueLock(false);
        }
    }, [hasActiveSyntheticJob]);

    const latestJob = useMemo(() =>
        jobs.find(j => j.status === 'completed' && getTestArtifact(j)) || jobs[0] || null,
        [jobs]);

    const loadTestCases = useCallback(async () => {
        const completedJobs = jobs.filter(j => j.status === 'completed');
        if (completedJobs.length === 0) return;

        setLoading(true);
        try {
            const allTestsMap: Record<string, RequirementWorkspaceTestCase> = {};
            let masterDirectory: RequirementTestFolderNode | null = null;

            // Fetch test cases from all completed jobs in parallel
            await Promise.all(completedJobs.map(async (job, index) => {
                const artifact = getTestArtifact(job);
                if (artifact) {
                    try {
                        const data = await fetchRequirementTestWorkspace(projectId, job.id, artifact.id);
                        if (data.tests && Array.isArray(data.tests)) {
                            data.tests.forEach((tc: any) => {
                                const uniqueKey = tc.test_key || tc.id;
                                if (uniqueKey) {
                                    allTestsMap[uniqueKey] = { ...tc, jobId: job.id };
                                }
                            });
                        }
                        // Use the directory from the most recent job that actually has one
                        if (data.directory && (!masterDirectory || index === 0)) {
                            masterDirectory = data.directory;
                        }
                    } catch (err) {
                        console.error(`Failed to load tests for job ${job.id}`, err);
                    }
                }
            }));

            setTestCases(Object.values(allTestsMap));
            if (masterDirectory) {
                setDirectory(masterDirectory);
                if (!selectedFolderId) {
                    setSelectedFolderId((masterDirectory as any).id);
                }
            }
        } catch (err: any) {
            console.error('Failed to load test cases', err);
        } finally {
            setLoading(false);
        }
    }, [jobs, projectId, selectedFolderId]);

    useEffect(() => {
        fetchRequirementJobs(projectId);
    }, [fetchRequirementJobs, projectId]);


    useEffect(() => {
        loadTestCases();
    }, [loadTestCases]);

    // Polling for jobs status
    useEffect(() => {
        const hasActiveJobs = jobs.some(j => isActiveSyntheticJobStatus(j.status));
        if (!hasActiveJobs) return;

        const interval = setInterval(() => {
            fetchRequirementJobs(projectId);
        }, 5000);
        return () => clearInterval(interval);
    }, [jobs, fetchRequirementJobs, projectId]);


    const handleToggleSelect = (key: string) => {
        const next = new Set(selectedTestKeys);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setSelectedTestKeys(next);
    };

    const handleSelectFolder = (folder: RequirementTestFolderNode) => {
        setSelectedFolderId(folder.id);
    };

    const selectedFolder = useMemo(() => {
        const findFolderById = (node: RequirementTestFolderNode, id: string): RequirementTestFolderNode | null => {
            if (node.id === id) return node;
            for (const child of node.children) {
                const found = findFolderById(child, id);
                if (found) return found;
            }
            return null;
        };

        if (!directory || !selectedFolderId) return null;
        return findFolderById(directory, selectedFolderId);
    }, [directory, selectedFolderId]);

    const highlightedFolderIds = useMemo(() => {
        const highlightedIds = new Set<string>();
        if (!directory || selectedTestKeys.size === 0) {
            return highlightedIds;
        }

        const collectHighlightedFolders = (folder: RequirementTestFolderNode): void => {
            const folderPath = folder.path || [];
            const containsSelectedTest = testCases.some((testCase) => (
                selectedTestKeys.has(testCase.test_key)
                && folderPath.every((segment, index) => testCase.folder_path?.[index] === segment)
            ));

            if (containsSelectedTest) {
                highlightedIds.add(folder.id);
            }
            folder.children.forEach(collectHighlightedFolders);
        };

        collectHighlightedFolders(directory);
        return highlightedIds;
    }, [directory, selectedTestKeys, testCases]);

    const filteredTestCases = useMemo(() => {
        let candidates = testCases;

        // Folder filter
        if (selectedFolder) {
            const folderPath = selectedFolder.path || [];
            candidates = candidates.filter(tc => {
                const tcPath = tc.folder_path || [];
                return folderPath.every((segment, i) => tcPath[i] === segment);
            });
        }

        // Search filter
        const query = search.trim().toLowerCase();
        if (query) {
            candidates = candidates.filter(tc =>
                (tc.title || '').toLowerCase().includes(query) ||
                (tc.test_key || '').toLowerCase().includes(query) ||
                (tc.description || '').toLowerCase().includes(query)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            candidates = candidates.filter(tc => (tc.latest_status || 'never_run').toLowerCase() === statusFilter);
        }

        // Sorting
        const sorted = [...candidates];
        sorted.sort((a, b) => {
            if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
            if (sortBy === 'priority') return Number(a.priority ?? 99) - Number(b.priority ?? 99);
            return 0;
        });

        return sorted;
    }, [selectedFolder, testCases, search, statusFilter, sortBy]);

    const handleSelectAllInFolder = () => {
        const next = new Set(selectedTestKeys);
        const allInFolderSelected = filteredTestCases.every(tc => next.has(tc.test_key));

        filteredTestCases.forEach(tc => {
            if (allInFolderSelected) {
                next.delete(tc.test_key);
            } else {
                next.add(tc.test_key);
            }
        });
        setSelectedTestKeys(next);
    };

    const handleGenerate = async () => {
        if (selectedTestKeys.size === 0) return;
        if (hasActiveSyntheticJob || syntheticQueueLock) {
            setMessage({
                type: 'danger',
                text: 'A synthetic data generation job is already running for this project. Please wait for it to complete before starting another one.',
            });
            return;
        }

        // NEW: Scan supporting context for excessive variant requests
        const contextNumbers = supportingContext.match(/\b\d{3,}\b/g); // Find any number with 3 or more digits
        if (contextNumbers) {
            const largeNum = Math.max(...contextNumbers.map(Number));
            if (largeNum > maxVariants) {
                const proceed = window.confirm(
                    `Warning: You mentioned a high number (${largeNum}) in your instructions. \n\nPlease note that the system limit is ${maxVariants} variants per test case. The AI will only generate up to ${maxVariants} rows. \n\nDo you want to proceed anyway?`
                );
                if (!proceed) return;
            }
        }

        const selectedTests = testCases.filter(tc => selectedTestKeys.has(tc.test_key));

        setSyntheticQueueLock(true);
        setGenerating(true);
        setMessage(null);
        try {
            await apiClient.post(API_ENDPOINTS.TEST_DATA_GENERATE(projectId), {
                display_id: selectedTests.map(tc => {
                    // Calculate short ID like E1-S2-TC7 to match Execution Tab
                    const epicId = tc.linked_epic_id || tc.linked_epic?.id;
                    const storyId = tc.linked_user_story_id || tc.linked_user_story?.id;
                    const testId = tc.test_case_id || tc.id;

                    const shortId = (epicId && storyId && testId)
                        ? `E${epicId}-S${storyId}-TC${testId}`
                        : tc.test_key;

                    return shortId;
                }),
                num_variants: numVariants,
                supporting_context: supportingContext,
                sample_file_content: sampleFileContent
            });
            setMessage({ type: 'success', text: 'Synthetic data generation job queued successfully. Open Generated Datasets to track it.' });
            setSelectedTestKeys(new Set());
            fetchRequirementJobs(projectId); // Refresh jobs immediately
        } catch (err: any) {
            setSyntheticQueueLock(false);
            setMessage({ type: 'danger', text: err?.response?.data?.detail || err?.message || 'Failed to trigger generation.' });
        } finally {
            setGenerating(false);
        }
    };

    const loadPreviewDetail = useCallback(
        async (testKey: string, showLoader = true) => {
            const artifact = latestJob ? getTestArtifact(latestJob) : null;
            if (!latestJob || !artifact) {
                setPreviewDetail(null);
                return;
            }

            if (showLoader) {
                setPreviewDetailLoading(true);
            }
            setPreviewDetailError(null);

            try {
                const response = await fetchRequirementTestDetail(
                    projectId,
                    latestJob.id,
                    artifact.id,
                    testKey,
                );
                setPreviewDetail(response);
            } catch (error: any) {
                setPreviewDetailError(error?.response?.data?.detail || error?.message || 'Failed to load test detail.');
            } finally {
                if (showLoader) {
                    setPreviewDetailLoading(false);
                }
            }
        },
        [latestJob, projectId],
    );

    const handlePreviewTest = useCallback(
        (testCase: RequirementWorkspaceTestCase) => {
            setPreviewTestKey(testCase.test_key);
            setPreviewDetail(null);
            setPreviewDetailError(null);
            void loadPreviewDetail(testCase.test_key);
        },
        [loadPreviewDetail],
    );

    const handleClosePreview = useCallback(() => {
        setPreviewTestKey(null);
        setPreviewDetail(null);
        setPreviewDetailError(null);
    }, []);

    const handleTestDeleted = useCallback(
        (testKey: string) => {
            setPreviewTestKey(null);
            setPreviewDetail(null);
            setSelectedTestKeys((previous) => {
                if (!previous.has(testKey)) {
                    return previous;
                }
                const next = new Set(previous);
                next.delete(testKey);
                return next;
            });
            loadTestCases();
        },
        [loadTestCases],
    );

    const handleTestEdited = useCallback(
        (testKey: string, updatedTestCase: Record<string, any>) => {
            setPreviewDetail((prev) => {
                if (!prev) return prev;
                return { ...prev, test: { ...prev.test, ...updatedTestCase } };
            });
            loadTestCases();
        },
        [loadTestCases],
    );

    const handleTestCaseCreated = useCallback(
        (_newTestCase: Record<string, any>) => {
            setShowAddTestCaseModal(false);
            loadTestCases();
        },
        [loadTestCases],
    );

    const handleOpenMoveDialog = useCallback((testCase: RequirementWorkspaceTestCase) => {
        setMoveDialog({
            open: true,
            test: testCase,
            value: formatFolderPathForDisplay(testCase.folder_path || []),
            saving: false,
            error: null,
        });
    }, []);

    const handleSaveMoveDialog = useCallback(async () => {
        const artifact = latestJob ? getTestArtifact(latestJob) : null;
        if (!latestJob || !artifact || !moveDialog.test) {
            return;
        }

        const parsedFolder = normalizeFolderPathInput(moveDialog.value);

        if (parsedFolder.length === 0) {
            setMoveDialog((previous) => ({ ...previous, error: 'Provide a folder path.' }));
            return;
        }

        setMoveDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            await updateRequirementTestFolder(
                projectId,
                latestJob.id,
                artifact.id,
                moveDialog.test.test_key,
                parsedFolder,
            );
            setMoveDialog({ open: false, test: null, value: '', saving: false, error: null });
            loadTestCases();
        } catch (error: any) {
            setMoveDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to update folder.',
            }));
        }
    }, [latestJob, moveDialog.test, moveDialog.value, projectId, loadTestCases]);

    const handleOpenRenameDialog = useCallback((folder: RequirementTestFolderNode) => {
        if ((folder.path || []).length <= 1) {
            return;
        }
        setRenameDialog({
            open: true,
            folder,
            value: folder.name || '',
            saving: false,
            error: null,
        });
    }, []);

    const handleSaveRenameDialog = useCallback(async () => {
        const artifact = latestJob ? getTestArtifact(latestJob) : null;
        if (!latestJob || !artifact || !renameDialog.folder) {
            return;
        }

        const newName = renameDialog.value.trim();
        if (!newName) {
            setRenameDialog((previous) => ({ ...previous, error: 'Provide a folder name.' }));
            return;
        }

        setRenameDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            const response = await renameRequirementTestFolder(
                projectId,
                latestJob.id,
                artifact.id,
                renameDialog.folder.path || [],
                newName,
            );
            setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null });
            await loadTestCases();
            setSelectedFolderId(response.folder_id);
        } catch (error: any) {
            setRenameDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to rename folder.',
            }));
        }
    }, [latestJob, loadTestCases, projectId, renameDialog.folder, renameDialog.value]);

    const handleOpenDeleteTestDialog = useCallback((testCase: RequirementWorkspaceTestCase) => {
        setDeleteTestDialog({ open: true, test: testCase, saving: false, error: null });
    }, []);

    const handleConfirmDeleteTestDialog = useCallback(async () => {
        const artifact = latestJob ? getTestArtifact(latestJob) : null;
        if (!latestJob || !artifact || !deleteTestDialog.test) {
            return;
        }
        const testKey = deleteTestDialog.test.test_key;
        setDeleteTestDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            await deleteRequirementTestCase(projectId, latestJob.id, artifact.id, testKey);
            setSelectedTestKeys((previous) => {
                if (!previous.has(testKey)) {
                    return previous;
                }
                const next = new Set(previous);
                next.delete(testKey);
                return next;
            });
            if (previewTestKey === testKey) {
                setPreviewTestKey(null);
                setPreviewDetail(null);
                setPreviewDetailError(null);
            }
            setDeleteTestDialog({ open: false, test: null, saving: false, error: null });
            loadTestCases();
        } catch (error: any) {
            setDeleteTestDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to delete the test case.',
            }));
        }
    }, [deleteTestDialog.test, latestJob, loadTestCases, previewTestKey, projectId]);

    const handleOpenDeleteFolderDialog = useCallback((folder: RequirementTestFolderNode) => {
        if ((folder.path || []).length <= 1) {
            return;
        }
        setDeleteFolderDialog({ open: true, folder, saving: false, error: null });
    }, []);

    const handleConfirmDeleteFolderDialog = useCallback(async () => {
        const artifact = latestJob ? getTestArtifact(latestJob) : null;
        if (!latestJob || !artifact || !deleteFolderDialog.folder) {
            return;
        }
        const folder = deleteFolderDialog.folder;
        const parentFolderId = (folder.path || []).slice(0, -1).join('/') || 'Results';
        setDeleteFolderDialog((previous) => ({ ...previous, saving: true, error: null }));
        try {
            const response = await deleteRequirementTestFolder(projectId, latestJob.id, artifact.id, folder.path || []);
            const deletedKeys = new Set(response.deleted_test_keys || []);
            setSelectedTestKeys((previous) => {
                if (previous.size === 0) {
                    return previous;
                }
                const next = new Set(Array.from(previous).filter((testKey) => !deletedKeys.has(testKey)));
                return next.size === previous.size ? previous : next;
            });
            if (previewTestKey && deletedKeys.has(previewTestKey)) {
                setPreviewTestKey(null);
                setPreviewDetail(null);
                setPreviewDetailError(null);
            }
            setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null });
            await loadTestCases();
            setSelectedFolderId(parentFolderId);
        } catch (error: any) {
            setDeleteFolderDialog((previous) => ({
                ...previous,
                saving: false,
                error: error?.response?.data?.detail || error?.message || 'Failed to delete the folder.',
            }));
        }
    }, [deleteFolderDialog.folder, latestJob, loadTestCases, previewTestKey, projectId]);

    const popupChatContext = useMemo(() => {
        const artifact = latestJob ? getTestArtifact(latestJob) : null;
        if (!previewDetail || !latestJob || !artifact) {
            return null;
        }
        return {
            context_mode: previewDetail.latest_run?.run_id ? 'run' : 'test',
            generation_id: latestJob.id,
            artifact_id: artifact.id,
            test_case_id: previewDetail.test.id ?? previewDetail.test.test_case_id ?? null,
            test_key: previewDetail.test.test_key,
            run_id: previewDetail.latest_run?.run_id ?? null,
        } as const;
    }, [previewDetail, latestJob]);

    const popupChat = previewDetail && latestJob && popupChatContext ? (
        <ProjectChatPanel
            key={`${previewDetail.test.test_key}:${previewDetail.latest_run?.run_id || 'test'}`}
            projectId={projectId}
            variant="embedded"
            title="Test Case Analysis"
            subtitle={previewDetail.latest_run?.run_id ? 'Scoped to the selected test and latest run.' : 'Scoped to the selected test.'}
            context={popupChatContext}
            allowMaximize
        />
    ) : null;

    if (loading && testCases.length === 0) {
        return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    return (
        <div className="rrw-page rrw-page-embedded">
            {/* Hero Section */}
            <div className="rrw-hero rrw-hero-embedded">
                <div>
                    <p className="rrw-hero-kicker">SYNTHETIC DATA GENERATION</p>
                    <h1 className="rrw-hero-title">Data Factory</h1>
                    <p className="rrw-hero-copy">
                        Select test cases from the directory and configure generation parameters to create realistic,
                        AI-driven synthetic datasets for your testing needs.
                    </p>
                </div>
            </div>

            {message && (
                <Alert
                    variant={message.type}
                    className="mb-4 shadow-sm border-0"
                    style={{ borderRadius: '14px' }}
                    dismissible
                    onClose={() => setMessage(null)}
                >
                    {message.text}
                </Alert>
            )}

            {/* Metrics Section */}
            <div className="rrw-metrics">
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Rows per Test</div>
                    <div className="rrw-metric-value">{numVariants}</div>
                    <div className="rrw-metric-note">Per test case</div>
                </div>
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Tests Selected</div>
                    <div className="rrw-metric-value">{selectedTestKeys.size}</div>
                    <div className="rrw-metric-note">Test cases selected</div>
                </div>
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Recent Generations</div>
                    <div className="rrw-metric-value">{syntheticJobs.length}</div>
                    <div className="rrw-metric-note">Across project</div>
                </div>
                <div className="rrw-metric-card">
                    <div className="rrw-metric-label">Setup Status</div>
                    <div className="rrw-metric-value">{sampleFile ? 'File Attached' : 'Clean'}</div>
                    <div className="rrw-metric-note text-truncate" style={{ maxWidth: '100%' }}>{sampleFile ? sampleFile.name : 'No reference file'}</div>
                </div>
            </div>

            {/* Main Workspace Layout */}
            <div className="rrw-workspace rrw-synthetic-workspace">
                {/* Sidebar Column: Directory */}
                <div className="rrw-column rrw-column-sidebar">
                    {/* Directory Panel */}
                    <div className="rrw-panel rrw-directory-panel">
                        <div className="rrw-panel-header">
                            <div>
                                <h3 className="rrw-panel-title">Test Directory</h3>
                                <p className="rrw-panel-subtitle">Filter scenarios by functional area.</p>
                            </div>
                        </div>
                        <div className="rrw-panel-content rrw-panel-content-scrollable" style={{ maxHeight: '400px', minHeight: '200px' }}>
                            {directory ? (
                                <div className="rrw-tree-wrapper">
                                    <TestDirectoryTree
                                        directory={directory}
                                        selectedFolderId={selectedFolderId}
                                        highlightedFolderIds={highlightedFolderIds}
                                        onSelectFolder={handleSelectFolder}
                                        onRenameFolder={handleOpenRenameDialog}
                                        onDeleteFolder={handleOpenDeleteFolderDialog}
                                    />
                                </div>
                            ) : (
                                <div className="p-4 text-center text-muted small">No folders available.</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Test List */}
                <div className="rrw-column rrw-column-results">
                    {/* Test List Panel */}
                    <div className="rrw-synthetic-list-container">
                        <TestResultsList
                            tests={filteredTestCases}
                            totalTestCount={testCases.length}
                            selectedFolderName={selectedFolder?.name}
                            selectedTestKey={previewTestKey}
                            selectedTestKeys={selectedTestKeys}
                            selectedCount={selectedTestKeys.size}
                            allVisibleSelected={filteredTestCases.length > 0 && filteredTestCases.every(tc => selectedTestKeys.has(tc.test_key))}
                            search={search}
                            statusFilter={statusFilter}
                            lastRunFilter="all"
                            sortBy={sortBy}
                            onSearchChange={setSearch}
                            onStatusFilterChange={setStatusFilter}
                            onLastRunFilterChange={() => { }}
                            onSortChange={setSortBy}
                            onSelectTest={(tc) => handleToggleSelect(tc.test_key)}
                            onToggleTestSelection={(tc) => handleToggleSelect(tc.test_key)}
                            onToggleVisibleSelection={handleSelectAllInFolder}
                            onClearSelection={() => setSelectedTestKeys(new Set())}
                            onMoveTest={handleOpenMoveDialog}
                            onPreviewTest={handlePreviewTest}
                            onDeleteTest={handleOpenDeleteTestDialog}
                            showStatusFilter={false}
                            showLastRunFilter={false}
                            extraActions={
                                <>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleGenerate}
                                        disabled={generating || syntheticQueueLock || hasActiveSyntheticJob || selectedTestKeys.size === 0 || isOverLimit}
                                        style={{ fontWeight: 600 }}
                                    >
                                        {generating || syntheticQueueLock || hasActiveSyntheticJob ? (
                                            <><Spinner as="span" animation="border" size="sm" className="me-2" />{generating || syntheticQueueLock ? 'Queueing...' : 'Generation in Progress'}</>
                                        ) : (
                                            <>
                                                <DatabaseFill size={14} className="me-2" />
                                                Generate Data ({selectedTestKeys.size})
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowCustomSettings(true)}
                                        style={{ fontWeight: 600 }}
                                    >
                                        <GearFill size={13} className="me-1" />
                                        Custom Settings
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => navigate(`/projects/${projectId}/test-data`)}
                                        style={{ fontWeight: 600 }}
                                    >
                                        <DatabaseFill size={13} className="me-1" />
                                        Generated Datasets
                                    </Button>
                                </>
                            }
                        />
                        {hasActiveSyntheticJob && (
                            <div className="text-muted small mt-2 px-2">
                                A synthetic data generation job is already in progress for this project. This button will be enabled again after that job completes.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Feature 7: Test case popup modal */}
            {previewTestKey && latestJob && getTestArtifact(latestJob) && (
                <TestCasePopup
                    testKey={previewTestKey}
                    projectId={projectId}
                    generationId={latestJob.id}
                    artifactId={getTestArtifact(latestJob)!.id}
                    detail={previewDetail}
                    detailLoading={previewDetailLoading}
                    detailError={previewDetailError}
                    onClose={handleClosePreview}
                    onDeleted={handleTestDeleted}
                    onEdited={handleTestEdited}
                    chatPanel={popupChat}
                    isSyntheticMode={true}
                />
            )}

            {/* Feature 7: Add test case modal */}
            {showAddTestCaseModal && latestJob && getTestArtifact(latestJob) && (
                <AddTestCaseModal
                    projectId={projectId}
                    generationId={latestJob.id}
                    artifactId={getTestArtifact(latestJob)!.id}
                    onClose={() => setShowAddTestCaseModal(false)}
                    onCreated={handleTestCaseCreated}
                />
            )}

            <Modal
                show={showCustomSettings}
                onHide={() => setShowCustomSettings(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                            Custom Settings
                        </Modal.Title>
                        <p className="rrw-settings-subtitle">
                            Tune the dataset instructions before queueing generation.
                        </p>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="rrw-synthetic-settings">
                        <div className="rrw-settings-card rrw-synthetic-settings-main">
                            <Form.Group>
                                <Form.Label className="rrw-settings-label">Custom Instructions</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    placeholder="Add instructions (e.g., 'Use realistic names', 'Prices should be positive')..."
                                    value={supportingContext}
                                    onChange={(e) => setSupportingContext(e.target.value)}
                                    className="rrw-settings-textarea"
                                />
                            </Form.Group>
                            <div className="upload-dropzone p-3 text-center border rounded-3 bg-light" style={{ borderStyle: 'dashed' }}>
                                <input type="file" className="d-none" id="factoryUploadSettings" accept=".csv,.tsv" onChange={handleFileUpload} />
                                <label htmlFor="factoryUploadSettings" className="mb-0 cursor-pointer w-100 py-2">
                                    <div className="fw-bold text-primary">
                                        {sampleFile ? (
                                            <><CheckCircle size={14} className="me-1 text-success" /> {sampleFile.name}</>
                                        ) : (
                                            <><Upload size={14} className="me-1" /> Reference File</>
                                        )}
                                    </div>
                                    <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                                        Allowed: .csv, .tsv
                                    </div>
                                </label>
                            </div>
                        </div>
                        <div className="rrw-settings-card rrw-synthetic-settings-side">
                            <Form.Group>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Form.Label className="rrw-settings-label m-0">Variants</Form.Label>
                                    <span className={`rrw-settings-limit ${isOverLimit ? 'text-danger fw-bold' : ''}`}>
                                        Max: {maxVariants}
                                    </span>
                                </div>
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    value={numVariants}
                                    min={1}
                                    max={maxVariants}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        setNumVariants(val > maxVariants ? maxVariants : val);
                                    }}
                                    className={`rrw-settings-number ${isOverLimit ? 'border-danger' : ''}`}
                                    isInvalid={isOverLimit}
                                />
                                <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                                    Limit is {maxVariants} per test case.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <div className="rrw-settings-estimate">
                                <div className="rrw-settings-estimate-label">Total Estimated Rows</div>
                                <div className={`rrw-settings-estimate-value ${totalRows > 500 ? 'text-warning' : ''}`}>
                                    {totalRows.toLocaleString()} Rows
                                </div>
                                <div className="rrw-settings-estimate-note">
                                    {selectedTestKeys.size} cases x {numVariants} variants
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="rrw-settings-footer">
                    <Button variant="outline-secondary" onClick={() => setShowCustomSettings(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setShowCustomSettings(false)}>
                        Save Settings
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={moveDialog.open}
                onHide={() => setMoveDialog({ open: false, test: null, value: '', saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Move Test</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Enter a slash-separated folder path.
                    </p>
                    <Form.Control
                        value={moveDialog.value}
                        onChange={(event) => setMoveDialog((previous) => ({ ...previous, value: event.target.value, error: null }))}
                        placeholder="All Test Cases / Regression / Authentication"
                    />
                    {moveDialog.error && <div className="rrw-modal-error" style={{ color: 'var(--rrw-danger)', marginTop: '8px', fontSize: '0.85rem' }}>{moveDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setMoveDialog({ open: false, test: null, value: '', saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => void handleSaveMoveDialog()} disabled={moveDialog.saving}>
                        {moveDialog.saving ? <Spinner animation="border" size="sm" /> : 'Save Folder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={renameDialog.open}
                onHide={() => setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Rename Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Rename {formatFolderPathForDisplay(renameDialog.folder?.path)}.
                    </p>
                    <Form.Group controlId="synthetic-rename-folder-name">
                        <Form.Label>Folder name</Form.Label>
                        <Form.Control
                            value={renameDialog.value}
                            onChange={(event) => setRenameDialog((previous) => ({ ...previous, value: event.target.value, error: null }))}
                            placeholder="Authentication"
                            disabled={renameDialog.saving}
                            autoFocus
                        />
                    </Form.Group>
                    {renameDialog.error && <div className="rrw-modal-error" style={{ color: 'var(--rrw-danger)', marginTop: '8px', fontSize: '0.85rem' }}>{renameDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setRenameDialog({ open: false, folder: null, value: '', saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => void handleSaveRenameDialog()} disabled={renameDialog.saving}>
                        {renameDialog.saving ? <Spinner animation="border" size="sm" /> : 'Rename Folder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteTestDialog.open}
                onHide={() => setDeleteTestDialog({ open: false, test: null, saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Test Case</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Delete {deleteTestDialog.test?.title || deleteTestDialog.test?.test_key}? This removes the test case and its active history, cache, search, execution-summary, and local ADO link data.
                    </p>
                    {deleteTestDialog.error && <div className="rrw-modal-error" style={{ color: 'var(--rrw-danger)', marginTop: '8px', fontSize: '0.85rem' }}>{deleteTestDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setDeleteTestDialog({ open: false, test: null, saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => void handleConfirmDeleteTestDialog()} disabled={deleteTestDialog.saving}>
                        {deleteTestDialog.saving ? <Spinner animation="border" size="sm" /> : 'Delete Test Case'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteFolderDialog.open}
                onHide={() => setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="rrw-modal-copy">
                        Delete {formatFolderPathForDisplay(deleteFolderDialog.folder?.path)} and {deleteFolderDialog.folder?.counts.total || 0} test case{(deleteFolderDialog.folder?.counts.total || 0) === 1 ? '' : 's'} inside it? This cannot be undone locally.
                    </p>
                    {deleteFolderDialog.error && <div className="rrw-modal-error" style={{ color: 'var(--rrw-danger)', marginTop: '8px', fontSize: '0.85rem' }}>{deleteFolderDialog.error}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setDeleteFolderDialog({ open: false, folder: null, saving: false, error: null })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={() => void handleConfirmDeleteFolderDialog()} disabled={deleteFolderDialog.saving}>
                        {deleteFolderDialog.saving ? <Spinner animation="border" size="sm" /> : 'Delete Folder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .rrw-panel-content-scrollable { overflow-y: auto; }
                .rrw-tree-wrapper { padding: 8px 0; }
                .cursor-pointer { cursor: pointer; }
                .rrw-empty-panel { padding: 40px; text-align: center; color: var(--rrw-muted); font-size: 0.9rem; }
                .rrw-test-card { border-radius: 14px; margin-bottom: 8px; }
                .rrw-column-sidebar { width: auto; }
                .rrw-synthetic-workspace {
                    grid-template-columns: minmax(250px, 300px) minmax(0, 1fr);
                }
                .rrw-synthetic-list-container .rrw-filter-bar {
                    grid-template-columns: minmax(0, 1fr) 176px;
                }
                .rrw-synthetic-list-container .rrw-results-list {
                    max-height: calc(100vh - 430px);
                }
                .rrw-synthetic-settings {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 280px;
                    gap: 16px;
                    align-items: stretch;
                }
                .rrw-settings-subtitle {
                    margin: 4px 0 0;
                    color: var(--rrw-muted);
                    font-size: 0.84rem;
                    font-weight: 400;
                }
                .rrw-settings-card {
                    padding: 16px;
                    border: 1px solid var(--rrw-border);
                    border-radius: 14px;
                    background: var(--rrw-surface-alt);
                }
                .rrw-synthetic-settings-main,
                .rrw-synthetic-settings-side {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .rrw-settings-label {
                    margin-bottom: 8px;
                    color: var(--rrw-text);
                    font-size: 0.86rem;
                    font-weight: 700;
                }
                .rrw-settings-textarea {
                    width: 100%;
                    min-height: 132px;
                    max-height: 220px;
                    padding: 12px 14px;
                    border: 1px solid rgba(29, 103, 216, 0.4);
                    border-radius: 12px;
                    background: #ffffff;
                    color: var(--rrw-text);
                    font-size: 0.9rem;
                    line-height: 1.5;
                    resize: vertical;
                    box-shadow: 0 0 0 3px rgba(29, 103, 216, 0.08);
                }
                .rrw-settings-textarea:focus,
                .rrw-settings-number:focus {
                    outline: none;
                    border-color: rgba(29, 103, 216, 0.4);
                    box-shadow: 0 0 0 3px rgba(29, 103, 216, 0.12);
                }
                .rrw-settings-number {
                    width: 100%;
                    height: 44px;
                    padding: 0 12px;
                    border: 1px solid rgba(29, 103, 216, 0.4);
                    border-radius: 12px;
                    background: #ffffff;
                    color: var(--rrw-text);
                    font-size: 0.98rem;
                    box-shadow: 0 0 0 3px rgba(29, 103, 216, 0.08);
                }
                .rrw-settings-limit {
                    color: var(--rrw-muted);
                    font-size: 0.78rem;
                }
                .rrw-settings-estimate {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 132px;
                    padding: 18px;
                    border: 1px solid var(--rrw-border);
                    border-radius: 12px;
                    background: #ffffff;
                    text-align: center;
                }
                .rrw-settings-estimate-label {
                    color: var(--rrw-muted);
                    font-size: 0.82rem;
                }
                .rrw-settings-estimate-value {
                    margin-top: 8px;
                    color: var(--rrw-text);
                    font-size: 1.35rem;
                    font-weight: 800;
                }
                .rrw-settings-estimate-note {
                    margin-top: 6px;
                    color: var(--rrw-muted);
                    font-size: 0.8rem;
                }
                .rrw-settings-footer {
                    padding-top: 12px;
                    padding-bottom: 12px;
                }
                @media (max-width: 1100px) {
                    .rrw-synthetic-workspace,
                    .rrw-synthetic-settings {
                        grid-template-columns: 1fr;
                    }
                    .rrw-column-sidebar {
                        width: auto;
                    }
                    .rrw-synthetic-list-container .rrw-filter-bar {
                        grid-template-columns: 1fr;
                    }
                    .rrw-synthetic-list-container .rrw-results-list {
                        max-height: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default SyntheticDataTab;
